import React, { useEffect, useRef, useState } from "react";
import { useContextApi } from "../../../context/ContextApi";
import { useShowAlert } from "../../../context/AlertContext";
import handleError from "../../../constants/handleError";
import { useNetwork } from "../../../context/NetworkContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import { CircularProgress } from "@mui/material";
import CustomerSelector from "../../../constants/CustomerSelector";
import { AddIcon, CheckIcon, XIcon } from "../../../constants/Icons";
const AddProject = ({
  editData,
  onClose,
  initialTitle = "",
  isActive,
  isBtnLoading,
  setBtnLoading,
  setProjects,
  onProjectCreated,
}) => {
  const showAlert = useShowAlert();
  const TitleRef = useRef();
  const { checkNetwork } = useNetwork();
  const [error, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: editData?.title || initialTitle,
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const { useEscapeKey } = useContextApi();
  useEscapeKey(onClose);

  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(() => {
      TitleRef.current?.focus();
    }, 150);
    return () => clearTimeout(timer);
  }, [isActive]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const errors = {};

    if (!formData?.title?.trim()) {
      errors.title = "Project title is required";
    }else if(!selectedCustomer){
       errors.customer = "Customer is required";
     }

    setErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!checkNetwork()) return;
    setBtnLoading((prev) => ({ ...prev, add: true }));

    try {
      if (editData) {
        // --- Edit/Update API Integration Flow ---
        const response = await AxiosInstance.put(
          `module/project/update/${editData._id}`,
          { name: formData.title },
        );

        const updatedItem = response.data.results || {
          ...editData,
          title: formData.title,
          name: formData.title,
        };

        setProjects((prev) =>
          prev.map((p) => (p._id === editData._id ? updatedItem : p)),
        );
        showAlert("success", "Project updated successfully");
      } else {
        // --- Add/Create API Integration Flow ---
        const response = await AxiosInstance.post(`module/project/add`, {
          name: formData.title,
          customerId: selectedCustomer?._id,
        });

        // Pulling the item payload from the server response
        const newProjectItem = response.data.results;

        // Force prepend fresh records to the top of your array instantly
        setProjects((prev) => [newProjectItem, ...prev]);
        onProjectCreated?.(newProjectItem);
        showAlert(
          "success",
          response?.data?.message || "Project added successfully",
        );
      }
      onClose();
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setBtnLoading((prev) => ({ ...prev, add: false }));
    }
  };

  useEffect(() => {
    setFormData({
      title: editData?.name || initialTitle,
    });
  }, [editData, initialTitle]);

  return (
    <div className="modal-overlay" style={{ zIndex: 1010 }}>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        id="JObCardModal"
        tabIndex="-1"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-md modal-dialog-centered">
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-header">
              <h5 className="modal-title me-2">
                {editData ? "Edit Project" : "Add Project"}{" "}
              </h5>
              <button
                type="button"
                className="btn-close border"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            {/* Form Setup handling submission execution automatically on Enter key */}
            <form onSubmit={handleSubmit}>
              <div className="modal-body bg-light">
                <div className="row g-2">
                  <div className="col-12">
                    <div className="mb-2">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        ref={TitleRef}
                        type="text"
                        className="form-control"
                        value={formData.title}
                        placeholder="Enter title"
                        autoComplete="off"
                        onChange={(e) => {
                          setErrors("");
                          setFormData({ title: e.target.value });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSubmit();
                          }
                        }}
                      />
                    </div>
                    {error.title && (
                      <div className="invalid-feedback">{error.title}</div>
                    )}
                  </div>
                      <div className="col-12">
                      <label className="form-label">
                        Select Customer <span className="text-danger">*</span>
                      </label>
                    <CustomerSelector  onSelectCustomer={setSelectedCustomer} setError={setErrors}/>
                    {error?.customer && (
                      <div className="invalid-feedback d-block mt-1">
                        {error.customer}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </form>
            <div className="modal-footer d-block">
              <button
                onClick={onClose}
                type="button"
                data-bs-dismiss="modal"
                className="btn btn-outline-secondary text-left"
              >
                <XIcon/> Close
              </button>
              {isBtnLoading.add ? (
                <button className="btn btn-project pb-0 text-right" disabled>
                  <CircularProgress size={22} className="text-white" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-sm btn-project  text-right"
                  onClick={handleSubmit}
                >
                  {editData ? (
                    <CheckIcon/>
                  ) : (
                    <AddIcon iClass={"me-1"}/>
                  )}
                  {editData ? "Update" : "Add"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProject;
